/* eslint-disable */
import React, { useCallback, useRef, useState } from 'react';
import './index.less';
import './index.en.less';

export interface ISafeBrowsingSlide {
  id?: string | number;
  title?: string;
  description?: string;
  icon?: string;
}

export interface ISafeBrowsingProps {
  title?: string;
  subTitle?: string;
  description?: string;
  sectionTitle?: string;
  sectionDescription?: string;
  slides?: ISafeBrowsingSlide[];
}

const SafeBrowsing: React.FunctionComponent<ISafeBrowsingProps> = ({
  title,
  subTitle,
  description,
  sectionTitle,
  sectionDescription,
  slides = [],
}) => {
  const n = slides.length;
  const [active, setActive] = useState(0);
  const dragRef = useRef({ startX: 0, dragging: false, moved: 0 });

  const goTo = useCallback(
    (idx: number) => {
      if (!n) return;
      setActive(((idx % n) + n) % n);
    },
    [n]
  );

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragRef.current = { startX: e.clientX, dragging: true, moved: 0 };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current.dragging) return;
    dragRef.current.moved = e.clientX - dragRef.current.startX;
  }, []);

  const onPointerUp = useCallback(() => {
    const { dragging, moved } = dragRef.current;
    dragRef.current.dragging = false;
    if (!dragging) return;
    if (moved <= -40) next();
    else if (moved >= 40) prev();
  }, [next, prev]);

  if (!n && !title && !subTitle && !description) return null;

  return (
    <div className="safe-browsing">
      {title && <h3 className="safe-browsing-title">{title}</h3>}

      {(subTitle || description) && (
        <div className="safe-browsing-intro">
          {subTitle && <h4 className="safe-browsing-subtitle">{subTitle}</h4>}
          {description && <p className="safe-browsing-desc">{description}</p>}
        </div>
      )}

      {(sectionTitle || sectionDescription) && (
        <div className="safe-browsing-section">
          {sectionTitle && (
            <h4 className="safe-browsing-section-title">{sectionTitle}</h4>
          )}
          {sectionDescription && (
            <p className="safe-browsing-section-desc">{sectionDescription}</p>
          )}
        </div>
      )}

      {n > 0 && (
        <div className="safe-browsing-carousel">
          <button
            className="safe-browsing-carousel-arrow safe-browsing-carousel-arrow--prev"
            onClick={prev}
            aria-label="Previous"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div
            className="safe-browsing-carousel-viewport"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          >
            <div className="safe-browsing-carousel-track">
              {slides.map((slide, i) => {
                const isActive = i === active;
                return (
                  <div
                    key={slide.id ?? i}
                    className={`safe-browsing-carousel-card${
                      isActive ? ' is-active' : ''
                    }`}
                    style={{
                      transform: `translateX(${(i - active) * 110}%)`,
                      opacity: isActive ? 1 : 0,
                      pointerEvents: isActive ? 'auto' : 'none',
                    }}
                  >
                    {slide.icon && (
                      <div className="safe-browsing-carousel-card-content-img">
                        <img src={slide.icon} alt="" />
                      </div>
                    )}
                    <div className="safe-browsing-carousel-card-content-content">
                      {slide.title && (
                        <h5 className="safe-browsing-carousel-card-title">
                          {slide.title}
                        </h5>
                      )}
                      <div className="safe-browsing-carousel-card-content-line"></div>
                      {slide.description && (
                        <p className="safe-browsing-carousel-card-desc">
                          {slide.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            className="safe-browsing-carousel-arrow safe-browsing-carousel-arrow--next"
            onClick={next}
            aria-label="Next"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          <div className="safe-browsing-carousel-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`safe-browsing-carousel-dot${
                  i === active ? ' is-active' : ''
                }`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(SafeBrowsing);
